import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import PropTypes from 'prop-types';
import { memo } from 'react';

import SchemaJsonLd from 'components/Seo/SchemaJsonLd';

import { toAbsoluteLink, toThreadCategoryLink, toThreadLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { formatDiscussionForumPostingAsJsonLd } from 'lib/schemas';
import { sliceWithEllipsis, toFormattedJsonLdString } from 'lib/string';

import LINKS from 'constants/links';

const THREAD_META_DESCRIPTION_CHAR_LIMIT = 48;

const ThreadSchema = ({
  threadCategoryName = null,
  threadCategorySlug = null,
  threadComments,
  threadLikes,
  threadPublished,
  threadSlug = null,
  threadTextRaw = null,
  threadTitle,
  userName,
}) => {
  const truncatedDescription = sliceWithEllipsis(
    threadTextRaw,
    THREAD_META_DESCRIPTION_CHAR_LIMIT
  );

  return (
    <>
      <NextSeo
        canonical={toAbsoluteLink(toThreadLink(threadSlug))}
        description={truncatedDescription}
        openGraph={{
          title: threadTitle,
          description: truncatedDescription,
        }}
        title={threadTitle}
      />
      {threadCategoryName && (
        <>
          <BreadcrumbJsonLd
            itemListElements={[
              {
                position: 1,
                name: placeholder('breadcrumbs.forum'),
                item: toAbsoluteLink(LINKS.FORUM),
              },
              {
                position: 2,
                name: threadCategoryName,
                item: toAbsoluteLink(toThreadCategoryLink(threadCategorySlug)),
              },
              {
                position: 3,
                name: toFormattedJsonLdString(threadTitle),
                item: toAbsoluteLink(toThreadLink(threadSlug)),
              },
            ]}
          />
          {threadTextRaw && (
            <SchemaJsonLd
              id="discussion-forum"
              schema={formatDiscussionForumPostingAsJsonLd({
                authorName: userName,
                commentsCount: threadComments,
                likesCount: threadLikes,
                publishedAt: threadPublished,
                textRaw: threadTextRaw,
                title: threadTitle,
              })}
            />
          )}
        </>
      )}
    </>
  );
};

ThreadSchema.propTypes = {
  threadCategoryName: PropTypes.string,
  threadCategorySlug: PropTypes.string,
  threadComments: PropTypes.number.isRequired,
  threadLikes: PropTypes.number.isRequired,
  threadPublished: PropTypes.string.isRequired,
  threadSlug: PropTypes.string,
  threadTextRaw: PropTypes.string,
  threadTitle: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
};

export default memo(ThreadSchema);
